import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ZestLogo } from "../ZestLogo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "../ui/dropdown-menu";
import {
  LayoutDashboard, Phone as PhoneIcon, ShoppingBag, UtensilsCrossed,
  Settings, LogOut, User, Menu, X, ChevronDown, Plug, Flame, Brain
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/calls", icon: PhoneIcon, label: "Calls" },
  { to: "/orders", icon: ShoppingBag, label: "Orders" },
  { to: "/menu", icon: UtensilsCrossed, label: "Menu" },
  { to: "/knowledge", icon: Brain, label: "Knowledge" },
];

const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/calls": "Calls",
  "/orders": "Orders",
  "/menu": "Menu",
  "/knowledge": "Knowledge Graph",
  "/settings": "Settings",
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  // Derive breadcrumb from path
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const pageTitle = PAGE_TITLES[`/${pathSegments[0]}`] || "Page";

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-[260px] bg-white border-r border-slate-100 flex flex-col transition-transform duration-200 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      }`} data-testid="app-sidebar">
        {/* Logo */}
        <div className="h-16 px-5 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center">
            <ZestLogo height="h-8" />
          </div>
          <button className="lg:hidden p-1.5 rounded-md hover:bg-slate-100 transition-colors" onClick={() => setSidebarOpen(false)}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5" data-testid="sidebar-nav">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                    isActive
                      ? "bg-primary/8 text-primary nav-active"
                      : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* System status */}
        <div className="px-3 pb-2">
          <div className="px-3 py-2.5 rounded-lg bg-green-50/80 border border-green-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 pulse-green" />
              <span className="text-[11px] font-semibold text-green-700 uppercase tracking-wider">AI Active</span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <Plug className="w-3 h-3 text-green-600" />
              <span className="text-[11px] text-green-600">POS Connected</span>
            </div>
          </div>
        </div>

        {/* User section */}
        <div className="p-3 border-t border-slate-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg hover:bg-slate-50 transition-colors text-left" data-testid="user-menu-trigger">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary text-xs font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium truncate text-slate-800">{user?.name || "User"}</p>
                  <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-1.5">
                <p className="text-xs font-medium text-slate-800">{user?.name}</p>
                <p className="text-[11px] text-slate-400">{user?.role || "Owner"}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { navigate("/settings"); setSidebarOpen(false); }} data-testid="settings-menu-item">
                <Settings className="w-3.5 h-3.5 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600" data-testid="logout-button">
                <LogOut className="w-3.5 h-3.5 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-100 h-14 flex items-center px-4 lg:px-8">
          <button className="lg:hidden p-2 -ml-2 mr-3 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{pageTitle}</span>
            {pathSegments.length > 1 && (
              <>
                <span className="text-slate-300">/</span>
                <span className="text-xs text-slate-400">Detail</span>
              </>
            )}
          </div>

          <div className="flex-1" />

          {/* Top-right status */}
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-green-50/80 text-green-700 border-green-200 text-[11px] font-medium hidden sm:flex" data-testid="ai-status-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5" />
              AI Active
            </Badge>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
